import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../api/client';
import { PRACTICE_TYPE_ICONS, type Pillar, type Practice } from '../api/types';
import {
  formatMinutesShort,
  formatNumber,
  formatPrice,
  practiceTypeLabel,
  traditionLabel,
  useJourneyLocale,
} from '../i18n/journey';
import { useUser } from '../state/UserContext';
import { type Tradition } from '../theme/tokens';

/** One pillar's ordered level path, plus the practices that belong to it. */
interface PillarPath {
  pillar: Pillar;
  levels: Practice[];
}

/** What a card shows. Derived ONLY from the server's `access` (plus whether the caller has
 * completed it) -- never recomputed from list position or `premiumLocked` (review finding 10),
 * so it always agrees with Today, the practice page, audio and session completion, including
 * after a Premium downgrade (a completed Premium level then reads as Premium-locked, which is
 * what the server enforces). */
type CardState = 'available' | 'done' | 'prerequisite_required' | 'premium_required' | 'unpublished';

function cardState(p: Practice, completed: Set<string>): CardState {
  if (p.access === 'available') return completed.has(p.practiceId) ? 'done' : 'available';
  return p.access;
}

const BADGES: Record<CardState, string> = {
  available: '',
  done: '✓',
  prerequisite_required: '🔒',
  premium_required: '💎',
  unpublished: '⏳',
};

/** A locked card's explanation: what's needed and how to continue, without any protected content
 * (the list never carries a locked level's transcript -- only its public description). */
function LockedPreview({
  practice,
  state,
  prerequisite,
  freeAlternative,
  wasCompleted,
  onOpen,
}: {
  practice: Practice;
  state: Exclude<CardState, 'available' | 'done'>;
  prerequisite: Practice | null;
  freeAlternative: Practice | null;
  wasCompleted: boolean;
  onOpen: (practiceId: string) => void;
}) {
  const { t, lang } = useJourneyLocale();
  return (
    <div className="practice-preview" id={`preview-${practice.practiceId}`}>
      <p className="practice-preview-description">{practice.description}</p>
      {state === 'prerequisite_required' && (
        <>
          <p>
            {t('practices.locked.oneAtATime')}{' '}
            {prerequisite ? (
              <Trans
                i18nKey="practices.locked.completeFirst"
                values={{ title: prerequisite.title }}
                components={{ strong: <strong /> }}
              />
            ) : (
              t('practices.locked.completePrevious')
            )}
          </p>
          {prerequisite && prerequisite.access === 'available' && (
            <button type="button" className="btn btn-secondary" onClick={() => onOpen(prerequisite.practiceId)}>
              {t('practices.locked.goTo', { title: prerequisite.title })}
            </button>
          )}
        </>
      )}
      {state === 'premium_required' && (
        <>
          <p>
            {wasCompleted && `${t('practices.locked.completedEarlier')} `}
            {t('practices.locked.premium', { price: formatPrice(lang) })}
          </p>
          <div className="practice-preview-actions">
            <Link to="/settings?tab=billing" className="btn btn-secondary">
              {t('practices.locked.seePremium')}
            </Link>
            {freeAlternative && (
              <button type="button" className="btn btn-ghost" onClick={() => onOpen(freeAlternative.practiceId)}>
                {t('practices.locked.tryFree', { title: freeAlternative.title })}
              </button>
            )}
          </div>
        </>
      )}
      {state === 'unpublished' && <p>{t('practices.locked.unpublished')}</p>}
    </div>
  );
}

function PracticeCard({
  practice,
  state,
  showLevel,
  expanded,
  isNext,
  onActivate,
}: {
  practice: Practice;
  state: CardState;
  showLevel: boolean;
  expanded: boolean;
  isNext: boolean;
  onActivate: () => void;
}) {
  const { t, lang } = useJourneyLocale();
  const locked = state !== 'available' && state !== 'done';
  const stateLabel = state === 'available' ? '' : t(`practices.state.${state}`);
  return (
    <button
      type="button"
      className={`level-node review-card-${state}${state === 'done' ? ' done' : ''}${locked ? ' locked' : ''}${state === 'premium_required' ? ' premium' : ''}${isNext ? ' current' : ''}`}
      onClick={onActivate}
      aria-expanded={locked ? expanded : undefined}
      aria-controls={locked ? `preview-${practice.practiceId}` : undefined}
    >
      <span className="level-node-badge" aria-hidden="true">
        {BADGES[state] || PRACTICE_TYPE_ICONS[practice.type]}
      </span>
      <span className="level-node-info">
        {isNext && <span className="level-node-next">{t('practices.upNext')}</span>}
        <span className="level-node-title">{practice.title}</span>
        <span className="level-node-meta">
          {showLevel && practice.level != null && (
            <>{t('practices.level', { level: formatNumber(lang, practice.level) })} &middot; </>
          )}
          {practiceTypeLabel(t, practice.type)} &middot; {formatMinutesShort(lang, practice.durationSeconds)}
          {stateLabel && <> &middot; {stateLabel}</>}
        </span>
      </span>
    </button>
  );
}

export function Practices() {
  const { me } = useUser();
  const { t, lang } = useJourneyLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selected, setSelected] = useState<Tradition | null>(null);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [pillars, setPillars] = useState<Pillar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const options = me?.traditions ?? [];

  // Default the tradition tab to `?tradition=` (e.g. from a calendar observance) when it's one of
  // the user's traditions, else their main practice, else the first.
  useEffect(() => {
    if (selected || options.length === 0) return;
    const requested = searchParams.get('tradition') as Tradition | null;
    if (requested && options.includes(requested)) {
      setSelected(requested);
      return;
    }
    setSelected(me?.primaryTradition && options.includes(me.primaryTradition) ? me.primaryTradition : options[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.join(','), me?.primaryTradition]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setExpandedId(null);

    Promise.all([
      api.getPractices(selected ? { tradition: selected } : {}),
      api.getPillars(selected ? { tradition: selected } : {}),
    ])
      .then(([practicesRes, pillarsRes]) => {
        if (cancelled) return;
        setPractices(practicesRes.practices);
        setPillars(pillarsRes.pillars);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : t('practices.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // `t` is read at failure time only; a language change must not refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, attempt]);

  // Pillar practices render as authored level ladders (ordered by the author-assigned `level`).
  const pillarPaths: PillarPath[] = useMemo(() => {
    if (pillars.length === 0) return [];
    return pillars
      .map((pillar) => ({
        pillar,
        levels: practices
          .filter((p) => p.pillarId === pillar.pillarId)
          .sort((a, b) => (a.level ?? 0) - (b.level ?? 0)),
      }))
      .filter((pp) => pp.levels.length > 0);
  }, [pillars, practices]);

  // Practices without authored ordering (no pillar, or a pillar not in this view) are standalone:
  // shown as a set to try in any order, never as a sequential level ladder (review finding 10).
  const standalone = useMemo(() => {
    const shownPillars = new Set(pillarPaths.map((pp) => pp.pillar.pillarId));
    return practices
      .filter((p) => !p.pillarId || !shownPillars.has(p.pillarId))
      .sort((a, b) => a.durationSeconds - b.durationSeconds || a.title.localeCompare(b.title));
  }, [practices, pillarPaths]);

  const completed = new Set(me?.completedPracticeIds ?? []);

  // A free, playable alternative for a Premium explanation: something available and not yet done
  // in this view, else something available to revisit.
  const freeAlternative =
    practices.find((p) => p.access === 'available' && !completed.has(p.practiceId)) ??
    practices.find((p) => p.access === 'available') ??
    null;

  const open = (practiceId: string) => navigate(`/practice/${practiceId}`);

  const renderCard = (p: Practice, showLevel: boolean, isNext = false) => {
    const state = cardState(p, completed);
    const locked = state !== 'available' && state !== 'done';
    const prerequisite =
      state === 'prerequisite_required' && p.pillarId && typeof p.level === 'number'
        ? practices.find((q) => q.pillarId === p.pillarId && q.level === (p.level as number) - 1) ?? null
        : null;
    return (
      <>
        <PracticeCard
          practice={p}
          state={state}
          showLevel={showLevel}
          expanded={expandedId === p.practiceId}
          isNext={isNext}
          onActivate={() => (locked ? setExpandedId(expandedId === p.practiceId ? null : p.practiceId) : open(p.practiceId))}
        />
        {locked && expandedId === p.practiceId && (
          <LockedPreview
            practice={p}
            state={state}
            prerequisite={prerequisite}
            freeAlternative={freeAlternative && freeAlternative.practiceId !== p.practiceId ? freeAlternative : null}
            wasCompleted={completed.has(p.practiceId)}
            onOpen={open}
          />
        )}
      </>
    );
  };

  return (
    <div>
      <h1 className="page-title">{t('practices.title')}</h1>
      <p className="page-subtitle">{t('practices.subtitle')}</p>

      {options.length > 1 && (
        <div className="tradition-tabs">
          {options.map((tradition) => (
            <button
              key={tradition}
              type="button"
              className={`tradition-tab${selected === tradition ? ' active' : ''}`}
              onClick={() => setSelected(tradition)}
            >
              {traditionLabel(t, tradition)}
            </button>
          ))}
        </div>
      )}

      {error && (
        <p className="error-text" role="alert">
          {error}{' '}
          <button type="button" className="btn-inline-link" onClick={() => setAttempt((a) => a + 1)}>
            {t('shared.tryAgain')}
          </button>
        </p>
      )}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : practices.length === 0 ? (
        !error && <div className="empty-state">{t('practices.empty')}</div>
      ) : (
        <>
          {/* Wide screens: each pillar is a row -- its title, description and progress in a sticky
              side rail, its levels in a grid beside it -- instead of one tall single-file ladder. */}
          {pillarPaths.map(({ pillar, levels }) => {
            const doneCount = levels.filter((p) => completed.has(p.practiceId)).length;
            const nextId = levels.find((p) => cardState(p, completed) === 'available')?.practiceId;
            return (
              <section key={pillar.pillarId} className="pillar-section">
                <div className="pillar-head">
                  <h2 className="pillar-title">
                    <span aria-hidden="true">{PRACTICE_TYPE_ICONS[pillar.practiceType]}</span> {pillar.title}
                  </h2>
                  <p className="pillar-description">{pillar.description}</p>
                  <div className="pillar-progress">
                    <span className="pillar-progress-bar" aria-hidden="true">
                      <span style={{ width: `${Math.round((doneCount / levels.length) * 100)}%` }} />
                    </span>
                    <span className="pillar-progress-label">
                      {t('practices.progress', { done: formatNumber(lang, doneCount), total: formatNumber(lang, levels.length) })}
                    </span>
                  </div>
                </div>
                <div className="level-path">
                  {levels.map((p, i) => {
                    const state = cardState(p, completed);
                    return (
                      <div key={p.practiceId} className="level-row">
                        {i > 0 && (
                          <span
                            className={`level-connector${state === 'available' || state === 'done' ? ' filled' : ''}`}
                            aria-hidden="true"
                          />
                        )}
                        {renderCard(p, true, p.practiceId === nextId)}
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}

          {standalone.length > 0 && (
            <section className="pillar-section">
              <div className="pillar-head">
                <h2 className="pillar-title">{pillarPaths.length > 0 ? t('practices.morePractices') : t('practices.title')}</h2>
                <p className="pillar-description">
                  {pillarPaths.length > 0 ? t('practices.standaloneAfterPaths') : t('practices.standaloneOnly')}
                </p>
              </div>
              <div className="standalone-grid">
                {standalone.map((p) => (
                  <div key={p.practiceId} className="standalone-item">
                    {renderCard(p, false)}
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
